import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { nanoid } from "nanoid";
import { useEditor, clamp, EDITOR_MODES, getAssetMaxDuration, isImageAsset } from "./store";
import { secondsToPx, pxToSeconds, getSnapPoints, snapClipPosition, snapLeftTrim, snapRightTrim, probeMedia, isImageType } from "./helpers";
import { cn } from "@/lib/utils/shadcn";
import { IoMdVolumeHigh, IoMdVolumeLow, IoMdVolumeOff } from "react-icons/io";
import { FaPlusCircle } from "react-icons/fa";
import { LuAudioLines, LuVideo } from "react-icons/lu";
import { useModal } from "@/contexts/ModalContext";
import { useShow } from "@/contexts/ShowContext";
import { apiFetch } from "@/lib/api";
import ScriptEditorModal from "@/components/modals/ScriptEditorModal";
import TimelineClip from "./TimelineClip";
import TimeRuler from "./TimeRuler";
import { FiPlus, FiZoomIn, FiZoomOut } from "react-icons/fi";

export function Timeline({ comments = [], onCommentMarkerClick }) {
  const tracks = useEditor((s) => s.tracks);
  const clips = useEditor((s) => s.clips);
  const assets = useEditor((s) => s.assets);
  const playhead = useEditor((s) => s.playhead);
  const zoom = useEditor((s) => s.zoom);
  const setZoom = useEditor((s) => s.setZoom);
  const moveClip = useEditor((s) => s.moveClip);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const mutedTracks = useEditor((s) => s.mutedTracks);
  const trackVolumes = useEditor((s) => s.trackVolumes);
  const setTrackVolume = useEditor((s) => s.setTrackVolume);
  const renameTrack = useEditor((s) => s.renameTrack);
  const mode = useEditor((s) => s.mode);
  const splitClipAtPosition = useEditor((s) => s.splitClipAtPosition);
  const selectedClipIds = useEditor((s) => s.selectedClipIds);
  const setSelectedClip = useEditor((s) => s.setSelectedClip);
  const addToSelection = useEditor((s) => s.addToSelection);
  const removeFromSelection = useEditor((s) => s.removeFromSelection);
  const toggleSelection = useEditor((s) => s.toggleSelection);
  const clearSelection = useEditor((s) => s.clearSelection);
  const removeClip = useEditor((s) => s.removeClip);
  const removeClips = useEditor((s) => s.removeClips);
  const moveClips = useEditor((s) => s.moveClips);
  const isPlaying = useEditor((s) => s.isPlaying);
  const setPlaying = useEditor((s) => s.setPlaying);
  const setMode = useEditor((s) => s.setMode);
  const snappingEnabled = useEditor((s) => s.snappingEnabled);
  const snapThreshold = useEditor((s) => s.snapThreshold);
  const toggleSnapping = useEditor((s) => s.toggleSnapping);
  const setPreviewTime = useEditor((s) => s.setPreviewTime);
  const showId = useEditor((s) => s.showId);
  const episodeId = useEditor((s) => s.episodeId);
  const segmentId = useEditor((s) => s.segmentId);
  const updateAsset = useEditor((s) => s.updateAsset);
  const addEmptyClipToTrack = useEditor((s) => s.addEmptyClipToTrack);
  const beginHistoryTransaction = useEditor((s) => s.beginHistoryTransaction);
  const commitHistoryTransaction = useEditor((s) => s.commitHistoryTransaction);
  const undo = useEditor((s) => s.undo);
  const redo = useEditor((s) => s.redo);

  const { openModal } = useModal();
  const { show } = useShow();

  const lanesContentRef = useRef(null);
  const rulerContentRef = useRef(null);
  const pendingMoveRef = useRef(null); // For throttling pointer move updates
  const [drag, setDrag] = useState(null);
  const [trim, setTrim] = useState(null); // { clipId, side: 'left'|'right', initialStart, initialIn, initialDuration, startX }
  const initialScrollLeft = useEditor.getState().timelineScrollLeft || 0;
  // Track scroll position via a ref (not React state) so rapid scroll events
  // don't re-render the whole Timeline on every frame. The ruler is kept in
  // sync imperatively in the scroll handlers, and we persist to the zustand
  // store on a throttled rAF tick.
  const scrollLeftRef = useRef(initialScrollLeft);
  const persistScrollFrameRef = useRef(null);
  const setScrollLeft = useCallback((value) => {
    scrollLeftRef.current = value;
    if (persistScrollFrameRef.current != null) return;
    persistScrollFrameRef.current = requestAnimationFrame(() => {
      persistScrollFrameRef.current = null;
      useEditor.getState().setTimelineScrollLeft(scrollLeftRef.current);
    });
  }, []);
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [editingTrackName, setEditingTrackName] = useState("");
  const [hoverX, setHoverX] = useState(null); // pixel position for hover indicator line
  const [snapLine, setSnapLine] = useState(null); // pixel position for snap indicator line
  const [volumePopoverTrackId, setVolumePopoverTrackId] = useState(null);
  const volumePopoverRef = useRef(null);
  const volumePopoverPos = useRef({ x: 0, y: 0 });
  const dragStartPosRef = useRef(null); // Track initial mouse position to detect if drag occurred
  const pendingSelectionResetRef = useRef(null); // Store clip ID that should be selected if no drag occurred

  // Compute timeline end
  const timelineEnd = useMemo(() => {
    let end = 20;
    for (const c of clips) end = Math.max(end, c.start + c.duration + 2);
    return end;
  }, [clips]);

  // Frame duration for arrow key navigation (assuming 30fps)
  const frameDuration = 1 / 30;

  const changeZoom = useCallback((delta) => {
    const newZoom = clamp(zoom + delta, 40, 240);
    if (newZoom === zoom || !lanesContentRef.current) return;

    const viewportWidth = lanesContentRef.current.clientWidth;
    const playheadPx = playhead * newZoom;
    const contentWidth = timelineEnd * newZoom;
    const maxScroll = Math.max(0, contentWidth - viewportWidth);
    const centeredScroll = clamp(playheadPx - viewportWidth / 2, 0, maxScroll);

    setZoom(newZoom);
    requestAnimationFrame(() => {
      if (lanesContentRef.current) {
        lanesContentRef.current.scrollLeft = centeredScroll;
      }
    });
  }, [playhead, setZoom, timelineEnd, zoom]);

  // Handle keyboard shortcuts
  useEffect(() => {
    function handleKeyDown(e) {
      // Don't trigger shortcuts if user is typing in an input/textarea
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA" || e.target.isContentEditable) {
        return;
      }

      // Don't handle delete/backspace when focus is inside the assets panel
      if (e.target.closest?.("[data-timeline-assets]")) {
        return;
      }

      // Undo / Redo
      if ((e.ctrlKey || e.metaKey) && (e.key === "z" || e.key === "Z")) {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === "y" || e.key === "Y")) {
        e.preventDefault();
        redo();
        return;
      }

      switch (e.key) {
        // Delete/Backspace - remove selected clips
        case "Delete":
        case "Backspace":
          if (selectedClipIds.length > 0) {
            e.preventDefault();
            if (selectedClipIds.length === 1) {
              removeClip(selectedClipIds[0]);
            } else {
              removeClips(selectedClipIds);
            }
          }
          break;

        // Space - toggle play/pause
        case " ":
          e.preventDefault();
          setPlaying(!isPlaying);
          break;

        // V - select mode
        case "v":
        case "V":
          e.preventDefault();
          setMode(EDITOR_MODES.SELECT);
          break;

        // C - cut mode
        case "c":
        case "C":
          e.preventDefault();
          setMode(EDITOR_MODES.CUT);
          break;

        // A - track select forward mode
        case "a":
        case "A":
          e.preventDefault();
          setMode(EDITOR_MODES.TRACK_SELECT_FORWARD);
          break;

        // S - toggle snapping (skip if Ctrl/Cmd held for save)
        case "s":
        case "S":
          if (e.ctrlKey || e.metaKey) break;
          e.preventDefault();
          toggleSnapping();
          break;

        // Right arrow - advance one frame
        case "ArrowRight":
          e.preventDefault();
          if (isPlaying) setPlaying(false);
          setPlayhead(playhead + frameDuration);
          break;

        // Left arrow - step back one frame
        case "ArrowLeft":
          e.preventDefault();
          if (isPlaying) setPlaying(false);
          setPlayhead(Math.max(0, playhead - frameDuration));
          break;

        // + or = - zoom in (centered on playhead)
        case "+":
        case "=": {
          e.preventDefault();
          changeZoom(40);
          break;
        }

        // - or _ - zoom out (centered on playhead)
        case "-":
        case "_": {
          e.preventDefault();
          changeZoom(-40);
          break;
        }

        default:
          break;
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedClipIds, removeClip, removeClips, isPlaying, setPlaying, setMode, playhead, setPlayhead, toggleSnapping, changeZoom, undo, redo]);

  const onPointerDownClip = useCallback((e, clip) => {
    e.stopPropagation();
    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    const timeAtPointer = pxToSeconds(pxX, zoom);

    // Read mode fresh from the store to avoid stale closures from memoized clips
    const currentMode = useEditor.getState().mode;

    // In cut mode, split the clip at the click position
    if (currentMode === EDITOR_MODES.CUT) {
      splitClipAtPosition(clip.id, timeAtPointer);
      return;
    }

    // In track select forward mode, select this clip and all clips after it on the same track
    if (currentMode === EDITOR_MODES.TRACK_SELECT_FORWARD) {
      const currentClips = useEditor.getState().clips;
      const forwardClipIds = currentClips
        .filter((c) => c.trackId === clip.trackId && c.start >= clip.start)
        .map((c) => c.id);
      useEditor.setState({ selectedClipIds: forwardClipIds });
      return;
    }

    // Check if Ctrl/Cmd is held
    const isMultiSelect = e.ctrlKey || e.metaKey;
    const currentSelectedClipIds = useEditor.getState().selectedClipIds;
    const isSelected = currentSelectedClipIds.includes(clip.id);

    // Store initial mouse position to detect if drag occurred
    dragStartPosRef.current = { x: e.clientX, y: e.clientY };
    pendingSelectionResetRef.current = null;

    // Calculate the new selection state (before updating state)
    let newSelectedIds;
    if (isMultiSelect) {
      // Ctrl/Cmd + click: toggle selection immediately
      if (isSelected) {
        // Remove from selection
        newSelectedIds = currentSelectedClipIds.filter(id => id !== clip.id);
        // Update state
        removeFromSelection(clip.id);
      } else {
        // Add to selection
        newSelectedIds = [...currentSelectedClipIds, clip.id];
        // Update state
        addToSelection(clip.id);
      }
    } else {
      // Regular click without Ctrl/Cmd
      if (isSelected && currentSelectedClipIds.length > 1) {
        // Clicking on an already-selected item when multiple are selected:
        // Don't change selection yet - wait to see if user drags
        // If they drag, keep all selected. If they just click, reset to this clip.
        newSelectedIds = currentSelectedClipIds;
        pendingSelectionResetRef.current = clip.id; // Store for potential reset on mouse up
        // Don't change selection state yet
      } else if (isSelected && currentSelectedClipIds.length === 1) {
        // Already selected and it's the only one, keep it selected
        newSelectedIds = [clip.id];
      } else {
        // Clicking on unselected item: reset selection to this clip immediately
        newSelectedIds = [clip.id];
        setSelectedClip(clip.id);
      }
    }

    // Determine which clips to drag
    // Always drag all clips in the new selection (including the clicked one)
    const clipsToDrag = newSelectedIds;

    // If no clips selected after toggle, don't start drag
    if (clipsToDrag.length === 0) {
      return;
    }

    // Calculate offset for the primary clip (the one being clicked)
    const offset = timeAtPointer - clip.start;

    // Store initial positions of all clips to drag
    const currentClips = useEditor.getState().clips;
    const initialPositions = clipsToDrag.map(id => {
      const c = currentClips.find(c => c.id === id);
      return c ? { id: c.id, start: c.start, trackId: c.trackId } : null;
    }).filter(Boolean);

    beginHistoryTransaction("move-clips");
    setDrag({
      type: "clips",
      clipIds: clipsToDrag,
      primaryClipId: clip.id,
      offset,
      originTrackId: clip.trackId,
      currentTrackId: clip.trackId,
      initialPositions,
    });

    e.currentTarget.setPointerCapture(e.pointerId);
  }, [zoom, splitClipAtPosition, setSelectedClip, addToSelection, removeFromSelection, beginHistoryTransaction]);

  const onPointerDownTrimLeft = useCallback((e, clip) => {
    e.stopPropagation();
    
    // Don't allow trimming in cut mode
    if (mode === EDITOR_MODES.CUT) return;
    
    // Don't allow trimming when multiple clips are selected
    if (selectedClipIds.length > 1) return;
    
    // Don't allow trimming if clip has allowTrimming set to false
    if (clip.allowTrimming === false) return;
    
    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    
    beginHistoryTransaction("trim-clip");
    setTrim({
      clipId: clip.id,
      side: "left",
      initialStart: clip.start,
      initialIn: clip.in,
      initialDuration: clip.duration,
      startX: pxX,
    });

    // Set preview time to show the frame at clip start (doesn't move playhead)
    setPreviewTime(clip.start);
  }, [mode, setPreviewTime, selectedClipIds.length, beginHistoryTransaction]);

  const onPointerDownTrimRight = useCallback((e, clip) => {
    e.stopPropagation();
    
    // Don't allow trimming in cut mode
    if (mode === EDITOR_MODES.CUT) return;
    
    // Don't allow trimming when multiple clips are selected
    if (selectedClipIds.length > 1) return;
    
    // Don't allow trimming if clip has allowTrimming set to false
    if (clip.allowTrimming === false) return;
    
    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    
    beginHistoryTransaction("trim-clip");
    setTrim({
      clipId: clip.id,
      side: "right",
      initialStart: clip.start,
      initialIn: clip.in,
      initialDuration: clip.duration,
      startX: pxX,
    });

    // Set preview time to show the frame at clip end (doesn't move playhead)
    setPreviewTime(clip.start + clip.duration - 0.001);
  }, [mode, setPreviewTime, selectedClipIds.length, beginHistoryTransaction]);

  function onPointerMove(e) {
    // Skip if no operation is active
    if (!trim && (!drag || drag.type !== "clips")) return;

    // Throttle updates using requestAnimationFrame
    // Store event data and process on next animation frame
    const eventData = {
      clientX: e.clientX,
      clientY: e.clientY,
      currentTargetRect: e.currentTarget.getBoundingClientRect(),
    };

    // Cancel any pending update
    if (pendingMoveRef.current) {
      cancelAnimationFrame(pendingMoveRef.current);
    }

    // Schedule update for next frame
    pendingMoveRef.current = requestAnimationFrame(() => {
      pendingMoveRef.current = null;
      processPointerMove(eventData);
    });
  }

  function processPointerMove(eventData) {
    const { clientX, clientY, currentTargetRect } = eventData;
    
    // Check if drag actually occurred (mouse moved significantly)
    if (dragStartPosRef.current && drag) {
      const dx = Math.abs(clientX - dragStartPosRef.current.x);
      const dy = Math.abs(clientY - dragStartPosRef.current.y);
      const dragThreshold = 5; // pixels
      
      if (dx > dragThreshold || dy > dragThreshold) {
        // Drag occurred - clear pending selection reset
        pendingSelectionResetRef.current = null;
      }
    }
    
    // Convert snap threshold from pixels to seconds
    const snapThresholdSeconds = pxToSeconds(snapThreshold, zoom);

    // Handle trim operations
    if (trim) {
      const rect = lanesContentRef.current.getBoundingClientRect();
      const pxX = clientX - rect.left + lanesContentRef.current.scrollLeft;
      const deltaPx = pxX - trim.startX;
      const deltaTime = pxToSeconds(deltaPx, zoom);

      const clip = clips.find((c) => c.id === trim.clipId);
      const asset = clip ? assets[clip.assetId] : null;
      // Images have infinite max duration - they can be extended to any length
      const assetDuration = getAssetMaxDuration(asset);

      // Get snap points excluding the current clip
      const snapPoints = snappingEnabled ? getSnapPoints(clips, trim.clipId) : [];

      if (trim.side === "left") {
        // Trimming from the left: adjust start, in, and duration
        // deltaTime > 0 means moving right (trimming more off the beginning)
        // deltaTime < 0 means moving left (extending/untrimming)
        
        let newIn = trim.initialIn + deltaTime;
        let newStart = trim.initialStart + deltaTime;
        let newDuration = trim.initialDuration - deltaTime;

        // Constraints:
        // 1. Cannot trim past in: 0 (can't go before asset starts)
        if (newIn < 0) {
          const adjustment = -newIn;
          newIn = 0;
          newStart = trim.initialStart + deltaTime + adjustment;
          newDuration = trim.initialDuration - deltaTime - adjustment;
        }

        // 2. Cannot make duration negative or too small
        const minDuration = 0.1; // minimum 100ms
        if (newDuration < minDuration) {
          const excess = minDuration - newDuration;
          newDuration = minDuration;
          newStart -= excess;
          newIn -= excess;
          // Re-clamp in to 0
          if (newIn < 0) {
            newStart += (-newIn);
            newIn = 0;
          }
        }

        // 3. Cannot start before 0 on timeline
        if (newStart < 0) {
          const adjustment = -newStart;
          newStart = 0;
          newIn += adjustment;
          newDuration -= adjustment;
        }

        // Apply snapping to the left edge (start)
        if (snappingEnabled && snapPoints.length > 0) {
          const { snappedStart, didSnap } = snapLeftTrim(newStart, snapPoints, snapThresholdSeconds);
          if (didSnap) {
            // Adjust all values based on the snap
            newStart = snappedStart;
            newIn = trim.initialIn + (newStart - trim.initialStart);
            newDuration = trim.initialDuration - (newStart - trim.initialStart);
            
            // Re-apply constraints after snapping
            if (newIn < 0) {
              newStart -= newIn;
              newDuration += newIn;
              newIn = 0;
            }
            if (newDuration < minDuration) {
              newDuration = minDuration;
            }
            
            setSnapLine(secondsToPx(snappedStart, zoom));
          } else {
            setSnapLine(null);
          }
        } else {
          setSnapLine(null);
        }

        moveClip(trim.clipId, { start: newStart, in: newIn, duration: newDuration });
        
        // Update preview time to show preview frame at the new start position (doesn't move playhead)
        setPreviewTime(newStart);
      } else {
        // Trimming from the right: only adjust duration
        // deltaTime > 0 means moving right (extending)
        // deltaTime < 0 means moving left (trimming)
        
        let newDuration = trim.initialDuration + deltaTime;

        // Constraints:
        // 1. Cannot extend past asset's total duration
        const maxDuration = assetDuration - trim.initialIn;
        if (newDuration > maxDuration) {
          newDuration = maxDuration;
        }

        // 2. Cannot make duration negative or too small
        const minDuration = 0.1; // minimum 100ms
        if (newDuration < minDuration) {
          newDuration = minDuration;
        }

        // Apply snapping to the right edge (end)
        if (snappingEnabled && snapPoints.length > 0) {
          const proposedEnd = trim.initialStart + newDuration;
          const { snappedEnd, didSnap } = snapRightTrim(proposedEnd, snapPoints, snapThresholdSeconds);
          if (didSnap) {
            newDuration = snappedEnd - trim.initialStart;
            // Re-apply constraints after snapping
            if (newDuration > maxDuration) {
              newDuration = maxDuration;
            }
            if (newDuration < minDuration) {
              newDuration = minDuration;
            }
            setSnapLine(secondsToPx(snappedEnd, zoom));
          } else {
            setSnapLine(null);
          }
        } else {
          setSnapLine(null);
        }

        moveClip(trim.clipId, { duration: newDuration });
        
        // Update preview time to show preview frame at the new end position (doesn't move playhead)
        // Use a small offset (1ms) before the end so the clip is still "active" and shows the last frame
        setPreviewTime(trim.initialStart + newDuration - 0.001);
      }
      return;
    }

    // Handle clip drag operations
    if (!drag || drag.type !== "clips") return;

    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = clientX - rect.left + lanesContentRef.current.scrollLeft;
    // Get Y relative to the lanes container (not the content)
    const pxY = clientY - currentTargetRect.top;

    // Get the primary clip's initial position
    const primaryInitialPos = drag.initialPositions.find(p => p.id === drag.primaryClipId);
    if (!primaryInitialPos) return;

    // Calculate new position for primary clip based on mouse position
    let t = Math.max(0, pxToSeconds(pxX, zoom) - drag.offset);

    // Check if we're dragging multiple clips
    const isMultiDrag = drag.clipIds.length > 1;
    
    // Get the primary clip to check asset type
    const primaryClip = clips.find((c) => c.id === drag.primaryClipId);
    const asset = primaryClip ? assets[primaryClip.assetId] : null;
    // Get current track type for empty clips
    const currentTrack = primaryClip ? tracks.find((t) => t.id === primaryClip.trackId) : null;

    // Check if all dragged clips originate from the same track
    const allSameTrack = isMultiDrag && drag.initialPositions.every(
      p => p.trackId === drag.initialPositions[0].trackId
    );

    // Apply snapping if enabled (snap based on primary clip)
    if (snappingEnabled && primaryClip) {
      // Get snap points excluding all clips being dragged
      const snapPoints = getSnapPoints(clips.filter(c => !drag.clipIds.includes(c.id)), null);
      const { start: snappedStart, didSnap } = snapClipPosition(t, primaryClip.duration, snapPoints, snapThresholdSeconds);
      t = snappedStart;
      
      if (didSnap) {
        // Determine which edge snapped for the indicator
        const startDist = Math.min(...snapPoints.map(p => Math.abs(t - p)));
        const endDist = Math.min(...snapPoints.map(p => Math.abs(t + primaryClip.duration - p)));
        if (startDist <= endDist) {
          setSnapLine(secondsToPx(t, zoom));
        } else {
          setSnapLine(secondsToPx(t + primaryClip.duration, zoom));
        }
      } else {
        setSnapLine(null);
      }
    } else {
      setSnapLine(null);
    }

    // Calculate delta for all clips based on initial position
    const deltaStart = t - primaryInitialPos.start;

    // Multi-drag with clips on different tracks: only allow horizontal movement
    if (isMultiDrag && !allSameTrack) {
      moveClips(drag.clipIds, deltaStart, undefined, drag.initialPositions, true);
    } else {
      // Single clip or multi-drag with all clips on the same track: allow track changes
      const rowH = 68;
      const currentTrackIndex = tracks.findIndex(t => t.id === drag.currentTrackId);
      if (currentTrackIndex === -1) {
        const fallbackIndex = clamp(Math.floor(pxY / rowH), 0, tracks.length - 1);
        const targetTrack = tracks[fallbackIndex];
        const effectiveAssetType = asset?.type === "image" ? "video" : asset?.type;
        const canMove = (asset && targetTrack.type === effectiveAssetType) || 
                       (!asset && currentTrack && targetTrack.type === currentTrack.type);
        if (canMove) {
          moveClips(drag.clipIds, deltaStart, targetTrack.id, drag.initialPositions, false);
          setDrag({ ...drag, currentTrackId: targetTrack.id });
        } else {
          moveClips(drag.clipIds, deltaStart, undefined, drag.initialPositions, false);
        }
        return;
      }
      
      const mouseTrackIndex = clamp(Math.floor(pxY / rowH), 0, tracks.length - 1);
      
      let targetTrackIndex = currentTrackIndex;
      if (mouseTrackIndex !== currentTrackIndex) {
        const currentLaneTop = currentTrackIndex * rowH;
        const currentLaneBottom = (currentTrackIndex + 1) * rowH;
        
        if (mouseTrackIndex > currentTrackIndex) {
          if (pxY >= currentLaneBottom) {
            targetTrackIndex = mouseTrackIndex;
          }
        } else {
          if (pxY < currentLaneTop) {
            targetTrackIndex = mouseTrackIndex;
          }
        }
      }
      
      const targetTrack = tracks[targetTrackIndex];
      const effectiveAssetType = asset?.type === "image" ? "video" : asset?.type;
      
      const canMove = (asset && targetTrack.type === effectiveAssetType) || 
                     (!asset && currentTrack && targetTrack.type === currentTrack.type);
      
      if (canMove) {
        if (targetTrack.id !== drag.currentTrackId) {
          setDrag({
            ...drag,
            currentTrackId: targetTrack.id,
          });
        }
        moveClips(drag.clipIds, deltaStart, targetTrack.id, drag.initialPositions, false);
      } else {
        moveClips(drag.clipIds, deltaStart, undefined, drag.initialPositions, false);
      }
    }
  }

  function onPointerUp(e) {
    // Cancel any pending animation frame
    if (pendingMoveRef.current) {
      cancelAnimationFrame(pendingMoveRef.current);
      pendingMoveRef.current = null;
    }
    
    // If there's a pending selection reset (user clicked on selected item but didn't drag)
    if (pendingSelectionResetRef.current && drag?.type === "clips") {
      // Reset selection to just the clicked clip
      setSelectedClip(pendingSelectionResetRef.current);
      pendingSelectionResetRef.current = null;
    }
    
    if (drag?.type === "clips") {
      setDrag(null);
    }
    if (trim) {
      // Clear the preview time when trimming ends
      setPreviewTime(null);
      setTrim(null);
    }
    if (drag?.type === "clips" || trim) {
      commitHistoryTransaction();
    }
    // Clear snap line when releasing
    setSnapLine(null);
    
    // Clear drag start position
    dragStartPosRef.current = null;
  }

  // Restore the lanes/ruler horizontal scroll on mount so re-entering the
  // timeline (e.g. after closing the sketch editor) preserves the viewport.
  useEffect(() => {
    if (initialScrollLeft <= 0) return;
    if (lanesContentRef.current) lanesContentRef.current.scrollLeft = initialScrollLeft;
    if (rulerContentRef.current) rulerContentRef.current.scrollLeft = initialScrollLeft;
  }, []);

  // Cleanup pending animation frame on unmount
  useEffect(() => {
    return () => {
      if (pendingMoveRef.current) {
        cancelAnimationFrame(pendingMoveRef.current);
      }
      if (persistScrollFrameRef.current != null) {
        cancelAnimationFrame(persistScrollFrameRef.current);
        useEditor.getState().setTimelineScrollLeft(scrollLeftRef.current);
      }
    };
  }, []);

  // Close volume popover on click outside
  useEffect(() => {
    if (!volumePopoverTrackId) return;
    function handleClickOutside(e) {
      if (volumePopoverRef.current && !volumePopoverRef.current.contains(e.target)) {
        setVolumePopoverTrackId(null);
      }
    }
    document.addEventListener("pointerdown", handleClickOutside);
    return () => document.removeEventListener("pointerdown", handleClickOutside);
  }, [volumePopoverTrackId]);

  function handleLanesScroll(e) {
    const newScrollLeft = e.target.scrollLeft;
    setScrollLeft(newScrollLeft);
    // Sync ruler scroll
    if (rulerContentRef.current) {
      rulerContentRef.current.scrollLeft = newScrollLeft;
    }
  }

  function handleRulerScroll(newScrollLeft) {
    setScrollLeft(newScrollLeft);
    // Sync lanes scroll
    if (lanesContentRef.current) {
      lanesContentRef.current.scrollLeft = newScrollLeft;
    }
  }

  // During playback, auto-follow the playhead before it exits the right edge.
  useEffect(() => {
    if (!isPlaying || !lanesContentRef.current) return;

    const followOffsetPx = 100;
    const lanesEl = lanesContentRef.current;
    const viewportWidth = lanesEl.clientWidth;
    const currentRightEdge = lanesEl.scrollLeft + viewportWidth;
    const playheadPx = secondsToPx(playhead, zoom);

    if (playheadPx <= currentRightEdge - followOffsetPx) return;

    const contentWidth = secondsToPx(timelineEnd, zoom);
    const maxScroll = Math.max(0, contentWidth - viewportWidth);
    const targetScroll = clamp(playheadPx - (viewportWidth - followOffsetPx), 0, maxScroll);

    if (Math.abs(targetScroll - lanesEl.scrollLeft) < 1) return;
    lanesEl.scrollLeft = targetScroll;
    if (rulerContentRef.current) {
      rulerContentRef.current.scrollLeft = targetScroll;
    }
    setScrollLeft(targetScroll);
  }, [isPlaying, playhead, timelineEnd, zoom]);

  function handleLanesMouseMove(e) {
    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    setHoverX(pxX);
  }

  function handleLanesMouseLeave() {
    setHoverX(null);
  }

  function handleLanesClick(e) {
    if (e.target.closest('[data-clip]')) {
      return;
    }
    clearSelection();
  }

  const [assetDragTarget, setAssetDragTarget] = useState(null); // { trackIndex, x, hoveredClipId }
  const assetDragCounterRef = useRef(0);

  function handleAssetDragEnter(e) {
    if (!e.dataTransfer.types.includes("application/timeline-asset")) return;
    assetDragCounterRef.current++;
  }

  function handleAssetDragOver(e) {
    if (!e.dataTransfer.types.includes("application/timeline-asset")) return;
    e.preventDefault();

    const dragIsAudio = e.dataTransfer.types.includes("application/timeline-asset-audio");

    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    const pxY = e.clientY - rect.top;
    const trackIndex = clamp(Math.floor(pxY / rowH), 0, tracks.length - 1);
    const targetTrack = tracks[trackIndex];
    const dropTime = Math.max(0, pxToSeconds(pxX, zoom));

    const trackTypeMatch = dragIsAudio
      ? targetTrack.type === "audio"
      : targetTrack.type === "video";

    if (!trackTypeMatch) {
      e.dataTransfer.dropEffect = "none";
      setAssetDragTarget(null);
      return;
    }

    const hitClip = clips.find((c) => {
      if (c.trackId !== targetTrack.id) return false;
      return dropTime >= c.start && dropTime <= c.start + c.duration;
    });

    e.dataTransfer.dropEffect = "copy";
    setAssetDragTarget({ trackIndex, x: pxX, hoveredClipId: hitClip?.id || null });
  }

  function handleAssetDragLeave(e) {
    assetDragCounterRef.current--;
    if (assetDragCounterRef.current <= 0) {
      assetDragCounterRef.current = 0;
      setAssetDragTarget(null);
    }
  }

  async function handleClipDoubleClick(clip, asset) {
    // Script clips: open script editor
    if (asset?.scriptId) {
      if (!showId || !episodeId || !segmentId) return;
      try {
        const scriptUrl = `/api/shows/${showId}/episodes/${episodeId}/segments/${segmentId}/scenes/${asset.sceneId}/script/${asset.scriptId}`;
        const res = await apiFetch(scriptUrl);
        if (!res.ok) return;
        const script = await res.json();

        const handleSaveScript = async (scriptObj, data) => {
          await apiFetch(
            `/api/shows/${showId}/episodes/${episodeId}/segments/${segmentId}/scenes/${scriptObj.scene}/script/${scriptObj._id}`,
            { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) }
          );
        };

        const handleRefreshScript = async () => {
          const r = await apiFetch(scriptUrl);
          if (!r.ok) return null;
          return await r.json();
        };

        await openModal(
          <ScriptEditorModal
            script={script}
            onSaveScript={handleSaveScript}
            onRefreshScript={handleRefreshScript}
          />,
          "side"
        );

        const updated = await handleRefreshScript();
        if (updated) {
          const latestAsset = useEditor.getState().assets[asset.id];
          const updates = {};
          const newName = updated.speech || updated.text || latestAsset.name;
          const newSpeech = updated.speech || "";
          if (newName !== latestAsset.name) updates.name = newName;
          if (newSpeech !== latestAsset.speech) updates.speech = newSpeech;
          if (updated.audio_url) {
            updates.url = updated.audio_url;
          }
          if (updated.audio_duration) {
            updates.duration = updated.audio_duration / 1000;
          }

          if (Object.keys(updates).length > 0) {
            updateAsset(asset.id, updates);
          }
          if (updates.duration) {
            const state = useEditor.getState();
            const clipsForAsset = state.clips.filter((c) => c.assetId === asset.id);
            for (const c of clipsForAsset) {
              state.updateClip(c.id, { in: 0, duration: updates.duration });
            }
          }
          useEditor.getState().updateScriptItem(asset.scriptId, {
            url: updated.audio_url || null,
            duration: updated.audio_duration || null,
            name: updated.speech || updated.text || asset.name,
            speech: updated.speech || "",
          });
        }
      } catch (e) {
        console.error("Failed to open script editor:", e);
      }
      return;
    }

    // Image clips / empty video clips: open sketch editor
    const isImage = isImageAsset(asset);
    const isEmptyVideoClip = !asset && tracks.find((t) => t.id === clip.trackId)?.type === "video";
    if (!isImage && !isEmptyVideoClip) return;

    useEditor.getState().openSketchEditor(clip.id);
  }

  async function handleAssetDrop(e) {
    e.preventDefault();
    assetDragCounterRef.current = 0;
    setAssetDragTarget(null);

    const data = e.dataTransfer.getData("application/timeline-asset");
    if (!data) return;

    let item;
    try {
      item = JSON.parse(data);
    } catch (err) {
      return;
    }

    if (item.type === "folder") return;

    const isScript = item.type === "script";
    if (!item.url && !isScript) return;

    const isStoryboard = item.type === "storyboard";
    const addToAssetsFolder = () => {
      if (isScript || isStoryboard) return;
      const { assetsFolder, currentAssetFolderId, addAssetFolderItem } = useEditor.getState();
      if ((item.id && assetsFolder.some((i) => i.id === item.id)) ||
          (item.url && assetsFolder.some((i) => i.url === item.url))) return;
      const SCRIPT_FOLDER_ID = "__script_folder__";
      const STORYBOARD_FOLDER_ID = "__storyboard_folder__";
      const STORYBOARD_PANEL_PREFIX = "__storyboard_panel__";
      const isLocked = currentAssetFolderId === SCRIPT_FOLDER_ID ||
        currentAssetFolderId === STORYBOARD_FOLDER_ID ||
        currentAssetFolderId?.startsWith(STORYBOARD_PANEL_PREFIX) ||
        (currentAssetFolderId && assetsFolder.find((i) => i.id === currentAssetFolderId)?.canDelete === false);
      const parentId = isLocked ? null : currentAssetFolderId;
      addAssetFolderItem({
        id: nanoid(),
        parentId,
        type: "upload",
        mediaType: item.mediaType || "none",
        name: item.name || "Untitled",
        canDelete: true,
        url: item.url || null,
        thumbnailUrl: item.thumbnailUrl || null,
        duration: null,
      });
    };

    const rect = lanesContentRef.current.getBoundingClientRect();
    const pxX = e.clientX - rect.left + lanesContentRef.current.scrollLeft;
    const pxY = e.clientY - rect.top;
    const dropTime = Math.max(0, pxToSeconds(pxX, zoom));
    const dropTrackIndex = clamp(Math.floor(pxY / rowH), 0, tracks.length - 1);
    const targetTrack = tracks[dropTrackIndex];

    const effectiveMediaType = isImageType(item.url, item.mediaType) ? "image" : item.mediaType;
    const meta = item.url
      ? await probeMedia(item.url, effectiveMediaType)
      : { duration: item.duration ? item.duration / 1000 : 2, type: "audio", isImage: false };

    const droppedIsAudio = meta.type === "audio";
    const droppedIsVisual = !droppedIsAudio;

    // Reject drops on wrong track types
    if (droppedIsAudio && targetTrack.type !== "audio") return;
    if (droppedIsVisual && targetTrack.type !== "video") return;

    // Check if we dropped on an existing clip
    const hitClip = clips.find((c) => {
      if (c.trackId !== targetTrack.id) return false;
      const clipEnd = c.start + c.duration;
      return dropTime >= c.start && dropTime <= clipEnd;
    });

    if (hitClip) {
      const hitAsset = assets[hitClip.assetId];
      const hitIsScript = !!hitAsset?.scriptId;

      // Cannot drop anything on a script element
      if (hitIsScript) return;

      const assetType = isScript ? "audio" : meta.type;
      const assetMediaType = isScript ? "audio" : item.mediaType;

      if (hitClip.assetId) {
        const updateAsset = useEditor.getState().updateAsset;
        updateAsset(hitClip.assetId, {
          url: item.url,
          type: assetType,
          duration: meta.duration,
          name: item.name,
          mediaType: assetMediaType,
          ...(!isScript && item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : { thumbnailUrl: undefined }),
          ...(meta.isImage ? { isImage: true, maxDuration: Infinity } : { isImage: false }),
          scriptId: undefined,
          sceneId: undefined,
          characterId: undefined,
          speech: undefined,
          sketchId: undefined,
        }, { recordHistory: true });
        if (hitClip.sketchId) {
          useEditor.getState().updateClip(hitClip.id, { sketchId: undefined });
        }
        addToAssetsFolder();
      } else {
        const newAssetId = nanoid();
        const addAsset = useEditor.getState().addAsset;
        const updateClip = useEditor.getState().updateClip;

        addAsset({
          id: newAssetId,
          url: item.url,
          type: assetType,
          duration: meta.duration,
          name: item.name,
          mediaType: assetMediaType,
          ...(!isScript && item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : {}),
          ...(meta.isImage ? { isImage: true, maxDuration: Infinity } : {}),
        });
        updateClip(hitClip.id, { assetId: newAssetId, sketchId: undefined }, { recordHistory: true });
        addToAssetsFolder();
      }
      return;
    }

    const addAsset = useEditor.getState().addAsset;
    const addClip = useEditor.getState().addClip;

    let assetId;

    if (isScript && item.scriptId) {
      const existing = useEditor.getState().findAssetByScriptId(item.scriptId);
      if (existing) {
        assetId = existing.id;
      }
    }

    if (!assetId) {
      assetId = nanoid();
      addAsset({
        id: assetId,
        url: item.url,
        type: meta.type,
        duration: meta.duration,
        name: item.name,
        ...(item.thumbnailUrl ? { thumbnailUrl: item.thumbnailUrl } : {}),
        mediaType: item.mediaType,
        ...(meta.isImage ? { isImage: true, maxDuration: Infinity } : {}),
        ...(isScript ? {
          scriptId: item.scriptId,
          sceneId: item.sceneId,
          characterId: item.characterId,
          speech: item.speech,
        } : {}),
      });
    }

    addClip({ assetId, trackId: targetTrack.id, start: dropTime, mediaWidth: meta.width ?? null, mediaHeight: meta.height ?? null });
    addToAssetsFolder();
  }

  const contentWidthPx = secondsToPx(timelineEnd, zoom);
  const rowH = 68;

  return (
    <div className="flex-shrink-0 flex-1 overflow-hidden">
      {/* Time Ruler */}
      <TimeRuler 
        timelineEnd={timelineEnd} 
        zoom={zoom} 
        onScroll={handleRulerScroll}
        rulerContentRef={rulerContentRef}
        comments={comments}
        onCommentMarkerClick={onCommentMarkerClick}
      />

      {/* Lanes - Two column layout */}
      <div 
        className="flex bg-background"
        style={{ height: tracks.length * rowH + 2 }}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      >
        {/* Fixed track labels column */}
        <div className="w-40 min-w-[160px] bg-zinc-950 border-r border-zinc-800 flex-shrink-0">
          {tracks.map((tr, i) => {
            const volume = trackVolumes[tr.id] ?? 1;
            const isMuted = volume === 0;
            const VolumeIcon = isMuted ? IoMdVolumeOff : volume < 0.5 ? IoMdVolumeLow : IoMdVolumeHigh;
            const isPopoverOpen = volumePopoverTrackId === tr.id;
            return (
              <div
                key={tr.id}
                className={`group/track px-2 flex items-center justify-between gap-2 ${i !== tracks.length - 1 ? "border-b border-secondary/40" : ""}`}
                style={{ height: rowH }}
              >
                <div className="flex items-center gap-1 min-w-0">
                  {editingTrackId === tr.id ? (
                    <input
                      className="text-xs text-secondary-foreground bg-input border border-secondary rounded px-1 py-0.5 outline-none w-full min-w-0"
                      value={editingTrackName}
                      onChange={(e) => setEditingTrackName(e.target.value)}
                      onBlur={() => {
                        const trimmed = editingTrackName.trim();
                        if (trimmed && trimmed !== tr.name) renameTrack(tr.id, trimmed);
                        setEditingTrackId(null);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.target.blur();
                        if (e.key === "Escape") setEditingTrackId(null);
                      }}
                      autoFocus
                      onFocus={(e) => e.target.select()}
                    />
                  ) : (
                      <span
                        className="text-xs text-secondary-foreground truncate select-none hover:text-secondary-foreground/80 cursor-pointer"
                        onDoubleClick={() => {
                          setEditingTrackId(tr.id);
                          setEditingTrackName(tr.name);
                        }}
                      >
                        {tr.name}
                      </span>
                  )}
                </div>
                {editingTrackId !== tr.id &&<div className="relative flex items-center gap-0.5">
                  <button
                      onClick={() => addEmptyClipToTrack(tr.id)}
                      className="text-[10px] relative hidden group-hover/track:flex items-center whitespace-nowrap p-0.5 rounded-sm gap-0.5 cursor-pointer bg-secondary text-secondary-foreground hover:bg-secondary/80"
                      title={`Add empty ${tr.type} clip at playhead`}
                    >
                      <FiPlus className="w-3 h-3" />
                      Clip
                  </button>
                  <button
                    data-volume-btn={tr.id}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => {
                      if (isPopoverOpen) {
                        setVolumePopoverTrackId(null);
                      } else {
                        const rect = e.currentTarget.getBoundingClientRect();
                        volumePopoverPos.current = {
                          x: rect.left + rect.width / 2,
                          y: rect.bottom,
                        };
                        setVolumePopoverTrackId(tr.id);
                      }
                    }}
                    className={cn(
                      "p-1 cursor-pointer outline-none ring-0 focus:ring-0",
                      isMuted
                        ? "text-red-500 hover:text-red-400"
                        : "text-secondary-foreground/60 hover:text-secondary-foreground"
                    )}
                    title={`Volume: ${Math.round(volume * 100)}%`}
                  >
                    <VolumeIcon className="w-3.5 h-3.5" />
                  </button>
                  {isPopoverOpen && createPortal(
                    <div
                      ref={volumePopoverRef}
                      className="fixed z-[9999] bg-zinc-900 border border-zinc-700 rounded-lg p-2 flex flex-col items-center gap-1 shadow-xl"
                      style={{
                        left: volumePopoverPos.current.x,
                        top: volumePopoverPos.current.y + 6,
                        transform: "translate(-50%, -100%)",
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <span className="text-[10px] text-secondary-foreground/70 tabular-nums">
                        {Math.round(volume * 100)}
                      </span>
                      <input
                        type="range"
                        min="0"
                        max="1"
                        step="0.01"
                        value={volume}
                        onChange={(e) => setTrackVolume(tr.id, parseFloat(e.target.value))}
                        className="volume-slider"
                        style={{
                          writingMode: "vertical-lr",
                          direction: "rtl",
                          width: 20,
                          height: 100,
                          accentColor: isMuted ? "#ef4444" : "var(--primary)",
                        }}
                      />
                      <button
                        className={cn(
                          "p-0.5 rounded cursor-pointer",
                          isMuted
                            ? "text-red-500 hover:text-red-400"
                            : "text-secondary-foreground/60 hover:text-secondary-foreground"
                        )}
                        onClick={() => setTrackVolume(tr.id, isMuted ? 1 : 0)}
                        title={isMuted ? "Unmute" : "Mute"}
                      >
                        <VolumeIcon className="w-3.5 h-3.5" />
                      </button>
                    </div>,
                    document.body
                  )}
                </div>}
              </div>
            );
          })}
        </div>

        {/* Scrollable lanes content */}
        <div
          ref={lanesContentRef}
          className="flex-1 overflow-x-auto overflow-y-hidden"
          // Disable native overscroll bounce so the lanes can't visually
          // translate past the boundary while the ruler (whose scrollLeft is
          // clamped to [0, maxScroll]) stays put.
          style={{ overscrollBehaviorX: "none" }}
          onScroll={handleLanesScroll}
          onMouseMove={handleLanesMouseMove}
          onMouseLeave={handleLanesMouseLeave}
          onClick={handleLanesClick}
          onDragEnter={handleAssetDragEnter}
          onDragOver={handleAssetDragOver}
          onDragLeave={handleAssetDragLeave}
          onDrop={handleAssetDrop}
        >
          <div style={{ width: contentWidthPx, height: tracks.length * rowH }} className="relative">
            {/* Track rows with grid lines */}
            {tracks.map((tr, i) => (
              <div
                key={tr.id}
                className={`absolute left-0 right-0 ${i !== tracks.length - 1 ? "border-b border-secondary/40" : ""}`}
                style={{ top: i * rowH, height: rowH }}
              >
                {/* Subtle grid lines (no time labels) */}
                {Array.from({ length: Math.ceil(timelineEnd) + 1 }).map((_, s) => (
                  <div
                    key={s}
                    className="absolute top-0 bottom-0 border-l border-zinc-800/30"
                    style={{ left: secondsToPx(s, zoom), width: 1 }}
                  />
                ))}
              </div>
            ))}

            {/* Clips */}
            {clips.map((c) => {
              const trIndex = tracks.findIndex((t) => t.id === c.trackId);
              const track = tracks[trIndex];
              const asset = assets[c.assetId];
              const isTrimming = trim?.clipId === c.id;
              const isDragging = drag?.clipIds?.includes(c.id) || false;
              const isCutMode = mode === EDITOR_MODES.CUT;
              const isSelected = selectedClipIds.includes(c.id);
              const isTrimmingDisabled = selectedClipIds.length > 1 || c.allowTrimming === false;
              // Only pass trimSide for the clip being trimmed to prevent all clips re-rendering
              const trimSide = isTrimming ? trim.side : null;
              const isAssetDragHovered = assetDragTarget?.hoveredClipId === c.id;
              
                return (
                <TimelineClip
                    key={c.id}
                  clip={c}
                  asset={asset}
                  trackType={track?.type}
                  trackIndex={trIndex}
                  rowH={rowH}
                  zoom={zoom}
                  isSelected={isSelected}
                  isDragging={isDragging}
                  isTrimming={isTrimming}
                  trimSide={trimSide}
                  isCutMode={isCutMode}
                  isTrimmingDisabled={isTrimmingDisabled}
                  isAssetDragHovered={isAssetDragHovered}
                  onClipPointerDown={onPointerDownClip}
                  onTrimLeftPointerDown={onPointerDownTrimLeft}
                  onTrimRightPointerDown={onPointerDownTrimRight}
                  onClipDoubleClick={handleClipDoubleClick}
                />
              );
            })}

            {/* Asset drag: track highlight + drop position line */}
            {assetDragTarget && !assetDragTarget.hoveredClipId && (
              <>
                {/* Track row highlight */}
                <div
                  className="absolute left-0 right-0 bg-primary/10 pointer-events-none z-[5]"
                  style={{ top: assetDragTarget.trackIndex * rowH, height: rowH }}
                />
                {/* Vertical drop position line */}
                <div
                  className="absolute w-[2px] bg-primary pointer-events-none z-[6] rounded-full"
                  style={{
                    left: assetDragTarget.x,
                    top: assetDragTarget.trackIndex * rowH + 4,
                    height: rowH - 8,
                  }}
                />
              </>
            )}

            {/* Hover indicator line - hide when snap line is visible */}
            {hoverX !== null && snapLine === null && (
              <div
                className={cn(
                  "absolute top-0 bottom-0 w-[1px] pointer-events-none z-5",
                  mode === EDITOR_MODES.CUT ? "bg-red-500" : "bg-secondary-foreground/40"
                )}
                style={{ left: hoverX }}
              />
            )}

            {/* Snap indicator line */}
            {snapLine !== null && (
              <div
                className="absolute top-0 bottom-0 w-[1px] bg-primary pointer-events-none z-20"
                style={{ left: snapLine }}
              />
            )}

            {/* Playhead */}
            <div
              className="absolute top-0 bottom-0 w-[2px] bg-primary pointer-events-none z-10"
              style={{ left: secondsToPx(playhead, zoom) }}
            />
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 px-2 border-t flex-shrink-0" style={{ height: 28 }}>
        <div className="flex-1 min-w-0" />
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <button
            className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
            onClick={() => changeZoom(-40)}
            disabled={zoom <= 40}
            title="Zoom out"
          >
            <FiZoomOut className="w-3.5 h-3.5" />
          </button>
          <button
            className="p-1 text-secondary-foreground hover:bg-secondary rounded cursor-pointer disabled:opacity-30 disabled:cursor-default"
            onClick={() => changeZoom(40)}
            disabled={zoom >= 240}
            title="Zoom in"
          >
            <FiZoomIn className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
